import {
    AccordionDetails,
    Box,
    Grid
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import StyledAccordion from '../../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../../components/Accordions/StyledAccordionSummary'
import DefaultSpacingGrid from '../../../../components/Containers/DefaultSpacingGrid'
import InfoDisplayItem from '../../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../../components/Typographies/LabelItem'
import { useLocationModal } from '../../hooks/useLocationModal'
import FlowSection from '../FlowSection'
import messages from '../messages'
import TransportIcon from '../TransportIcon'
import { WaypointType } from '../types'
import ConsignmentServiceTable from './ConsignmentServiceTable'

interface ServiceWaypointProps {
    consignmentIndex: number
    waypointType: WaypointType
    customerId: number
    waypointId?: number
}

const ServiceWaypoint: React.FC<ServiceWaypointProps> = ({ consignmentIndex, waypointType, waypointId, customerId }) => {
    const { watch } = useFormContext()
    const [expanded, setExpanded] = useState(true)
    const { formatMessage } = useIntl()

    const handleToggleAccordion = () => {
        setExpanded((prev) => !prev)
    }

    const waypointPrefix = useMemo(() => {
        if (waypointType !== WaypointType.Waypoint) {
            return waypointType
        }
        return `waypoints.${waypointId}`

    }, [consignmentIndex])


    const {
        locationOptions,
    } = useLocationModal({
        onSave: () => {
            // Do something
        },
        customerId,
    })

    const locationId = watch(`bookings.${consignmentIndex}.${waypointPrefix}.locationId`)
    const transportMode = watch(`bookings.${consignmentIndex}.${waypointPrefix}.transitDetails.modeOfTransit`)

    const locationName = useMemo(() => {
        const location = locationOptions.find((loc: any) => loc.value === locationId)


        return location?.label || ''
    }, [locationId, locationOptions])

    const serviceTitle = useMemo(() => {
        switch (waypointType) {
            case WaypointType.ShipFrom:
                return formatMessage(messages.shipFrom) || 'Ship From'
            case WaypointType.DeliveryTo:
                return formatMessage(messages.deliverTo) || 'Ship To'
            case WaypointType.Waypoint:
                return formatMessage(messages.waypoint) || 'Waypoint'
            default:
                return ''
        }
    }, [waypointType])

    const getScheduledWaypointTypeName = useMemo(() => {
        switch (waypointType) {
            case WaypointType.ShipFrom:
                return 'loadingDate'
            case WaypointType.DeliveryTo:
                return 'deliveryDate'
            case WaypointType.Waypoint:
                return 'loadingDate'
            default:
                return ''
        }
    }, [waypointType])

    const scheduledDate = dayjs(watch(`bookings.${consignmentIndex}.${waypointPrefix}.${getScheduledWaypointTypeName}`)).format('YYYY-MM-DD')

    return (
        <FlowSection hasBorder={waypointType !== WaypointType.DeliveryTo} icon={transportMode !== undefined ? <TransportIcon id={transportMode} /> : undefined}>
            <Box sx={{ mb: waypointType === WaypointType.DeliveryTo ? '0px' : '16px' }}>
                <StyledAccordion
                    defaultExpanded
                    onChange={handleToggleAccordion}
                    sx={theme => ({ marginTop: '10px', pb: '4px' })}
                >
                    <StyledAccordionSummary
                        index={`service-waypoint-${consignmentIndex}`}
                        expanded={expanded}
                        title={serviceTitle}

                    >
                        <Grid container>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <LabelItem>{waypointType === WaypointType.Waypoint ? formatMessage(messages.waypointIdx, { idx: (waypointId ?? 0) + 1 }) : serviceTitle}</LabelItem>
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.locationName) || 'Location'}
                                    value={locationName}
                                    isSmallScreen={false}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <InfoDisplayItem
                                    label={formatMessage(messages.requestedDate) || 'Scheduled Date'}
                                    value={scheduledDate || '-'}
                                    isSmallScreen={false}
                                />
                            </Grid>
                        </Grid>
                    </StyledAccordionSummary>
                    <AccordionDetails>
                        <DefaultSpacingGrid container>
                            <Grid item xs={12}>
                                <ConsignmentServiceTable
                                    consignmentIndex={consignmentIndex}
                                    waypointType={waypointType}
                                    waypointPrefix={waypointPrefix}
                                />
                            </Grid>
                        </DefaultSpacingGrid>
                    </AccordionDetails>
                </StyledAccordion>
            </Box>
        </FlowSection>
    )
}

export default ServiceWaypoint
